import produce from 'immer';
import type { WritableDraft } from 'immer/dist/internal';
import { MetricEntry } from 'common/query-builder/query-entry-section/types';
import { FunnelOrder } from 'common/types/reports/bookmark';
import { SVG_ICONS } from 'common/components/svg-icon/icons';
import type { RowProps } from '../catalog';

/**
 * Returns true if the behavior at the given index is set to FunnelOrder.Any.
 * @param index
 * @returns boolean
 */
const checkBehaviorIsAnyOrder = (entry: MetricEntry | WritableDraft<MetricEntry>, index: number): boolean => {
  var _entry$behavior$behav;
  return ((_entry$behavior$behav = entry.behavior.behaviors) === null || _entry$behavior$behav === void 0 ? void 0 : _entry$behavior$behav[index].funnelOrder) === FunnelOrder.Any;
};

/**
 * Toggles the funnel order type for the given behavior at the given index
 * between FunnelOrder.Any and FunnelOrder.Loose.
 *
 * NOTE: Toggling the funnel order for a nested event comparison behavior just
 * toggles it on the parent behavior.
 * @param index
 * @returns void
 */
const _toggleAnyOrder = (entry: MetricEntry, index: number, onUpdate: RowProps[`updateEntry`]): void => {
  const updatedEntry = produce(entry, ent => {
    if (!ent.behavior.behaviors) {
      return ent;
    }
    ent.behavior.behaviors[index].funnelOrder = checkBehaviorIsAnyOrder(ent, index) ? FunnelOrder.Loose : FunnelOrder.Any;

    // If all behaviors are Loose then the parent behavior should be Loose otherwise
    // it should be Any.
    // @todo(metrics): This is handled on the backend by transform_multi_metric_query_params_to_funnels_bookmark.
    // Can we just remove all client code and rely on backend to compute this?
    ent.behavior.funnelOrder = ent.behavior.behaviors.every(behavior => behavior.funnelOrder === FunnelOrder.Loose) ? FunnelOrder.Loose : FunnelOrder.Any;
    return ent;
  });
  onUpdate(updatedEntry);
};
export function useFunnelAnyOrderIconToggle(entry: MetricEntry, behaviorIndex: number, onUpdate: RowProps[`updateEntry`]): {
  toggleAnyOrder: () => void;
  rowIcon: keyof typeof SVG_ICONS;
} {
  const behaviorIsAnyOrder = checkBehaviorIsAnyOrder(entry, behaviorIndex);
  return {
    toggleAnyOrder: () => _toggleAnyOrder(entry, behaviorIndex, onUpdate),
    rowIcon: behaviorIsAnyOrder ? "behavior-event-any-order" : "behavior-event"
  };
}